import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, X, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { products, getCart, addToCart as addToCartData, removeFromCart, updateCartQuantity } from '../data/siteData';

const ShopPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    // Load cart from localStorage
    setCart(getCart());
    
    // Listen for cart open event from navbar
    const handleOpenCart = () => setCartOpen(true);
    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []);

  const handleAddToCart = (product, quantity = 1) => {
    const existingItem = cart.find((item) => item.id === product.id);
    const maxQuantity = product.maxQuantity || 10;

    if (existingItem && existingItem.quantity >= maxQuantity) {
      toast.info(i18n.language === 'bg' ? 'Максималното количество е 10 броя' : 'The maximum quantity is 10');
      return;
    }

    const allowedQuantity = Math.min(quantity, maxQuantity - (existingItem?.quantity || 0));
    const updatedCart = addToCartData(product, allowedQuantity);
    setCart(updatedCart);
    setSelectedProduct(null);
    setSelectedQuantity(1);
    toast.success(i18n.language === 'bg' ? 'Добавено в количката' : 'Added to cart');
  };

  const openProductDetails = (product) => {
    setSelectedProduct(product);
    setSelectedImageIndex(0);
    setSelectedQuantity(1);
  };

  const handleRemoveFromCart = (productId) => {
    const updatedCart = removeFromCart(productId);
    setCart(updatedCart);
  };

  const handleUpdateQuantity = (productId, change) => {
    const item = cart.find(i => i.id === productId);
    if (!item) return;
    
    const newQuantity = item.quantity + change;
    if (newQuantity <= 0) {
      handleRemoveFromCart(productId);
    } else if (newQuantity > (item.maxQuantity || 10)) {
      toast.info(i18n.language === 'bg' ? 'Максималното количество е 10 броя' : 'The maximum quantity is 10');
    } else {
      const updatedCart = updateCartQuantity(productId, newQuantity);
      setCart(updatedCart);
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error(i18n.language === 'bg' ? 'Количката е празна' : 'Cart is empty');
      return;
    }
    
    setCartOpen(false);
    navigate(`/${i18n.language}/checkout`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-32">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-6">
            {t('shop.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('shop.subtitle')}
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            {i18n.language === 'bg' ? 'Плащане при получаване от куриера (наложен платеж).' : 'Pay the courier on delivery.'}
          </p>
        </motion.div>

        {/* Floating Cart Button */}
        <button
          onClick={() => setCartOpen(true)}
          data-testid="open-cart-button"
          className="fixed bottom-24 right-6 z-40 bg-accent text-accent-foreground p-4 rounded-full shadow-lg hover:bg-primary hover:text-primary-foreground transition-all"
        >
          <ShoppingCart className="w-6 h-6" />
          {getTotalItems() > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs w-6 h-6 flex items-center justify-center rounded-full">
              {getTotalItems()}
            </span>
          )}
        </button>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer"
              onClick={() => openProductDetails(product)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openProductDetails(product);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`${i18n.language === 'bg' ? 'Виж информация за' : 'View details for'} ${i18n.language === 'bg' ? product.name_bg : product.name}`}
            >
              <div className="bg-card border border-border overflow-hidden rounded-lg">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={product.image}
                    alt={i18n.language === 'bg' ? product.name_bg : product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                    {i18n.language === 'bg' ? product.name_bg : product.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {i18n.language === 'bg' ? product.description_bg : product.description}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <span className="font-heading text-2xl font-bold text-accent">
                      €{product.price.toFixed(2)}
                    </span>
                    <button
                      onClick={(event) => { event.stopPropagation(); handleAddToCart(product); }}
                      data-testid={`add-to-cart-${index}`}
                      className="w-full sm:w-auto bg-accent text-accent-foreground font-medium px-6 py-3 rounded-md hover:bg-primary hover:text-primary-foreground transition-all whitespace-nowrap"
                    >
                      {t('shop.addToCart')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Product Details */}
        <Dialog
          open={Boolean(selectedProduct)}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedProduct(null);
              setSelectedQuantity(1);
            }
          }}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
            {selectedProduct && (
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="min-w-0 bg-secondary p-4">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={(selectedProduct.images || [selectedProduct.image])[selectedImageIndex]}
                      alt={`${i18n.language === 'bg' ? selectedProduct.name_bg : selectedProduct.name} — ${selectedImageIndex + 1}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  {selectedProduct.images?.length > 1 && (
                    <div className="mt-3 flex gap-3" role="group" aria-label={i18n.language === 'bg' ? 'Снимки на продукта' : 'Product photos'}>
                      {selectedProduct.images.map((image, index) => (
                        <button
                          key={image}
                          type="button"
                          onClick={() => setSelectedImageIndex(index)}
                          aria-label={`${i18n.language === 'bg' ? 'Снимка' : 'Photo'} ${index + 1}`}
                          aria-pressed={selectedImageIndex === index}
                          className={`h-20 w-20 overflow-hidden border-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${selectedImageIndex === index ? 'border-accent' : 'border-transparent hover:border-border'}`}
                        >
                          <img src={image} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-8 flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl md:text-3xl text-foreground pr-8">
                      {i18n.language === 'bg' ? selectedProduct.name_bg : selectedProduct.name}
                    </DialogTitle>
                    <DialogDescription className="text-base leading-relaxed pt-4">
                      {i18n.language === 'bg' ? selectedProduct.description_bg : selectedProduct.description}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="mt-6 border-y border-border py-5">
                    <p className="font-heading text-3xl font-bold text-accent mb-5">
                      €{selectedProduct.price.toFixed(2)}
                    </p>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-medium text-foreground">
                        {i18n.language === 'bg' ? 'Количество' : 'Quantity'}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setSelectedQuantity((quantity) => Math.max(1, quantity - 1))}
                          disabled={selectedQuantity <= 1}
                          className="w-10 h-10 flex items-center justify-center border border-border rounded hover:bg-secondary disabled:opacity-40"
                          aria-label={i18n.language === 'bg' ? 'Намали количеството' : 'Decrease quantity'}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center text-lg font-bold text-foreground">
                          {selectedQuantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedQuantity((quantity) => Math.min(selectedProduct.maxQuantity || 10, quantity + 1))}
                          disabled={selectedQuantity >= (selectedProduct.maxQuantity || 10)}
                          className="w-10 h-10 flex items-center justify-center border border-border rounded hover:bg-secondary disabled:opacity-40"
                          aria-label={i18n.language === 'bg' ? 'Увеличи количеството' : 'Increase quantity'}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-right mt-2">
                      {i18n.language === 'bg' ? 'Максимум 10 броя' : 'Maximum 10 items'}
                    </p>
                  </div>

                  <div className="mt-auto pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-muted-foreground">
                        {i18n.language === 'bg' ? 'Общо' : 'Total'}
                      </span>
                      <span className="font-heading text-2xl font-bold text-foreground">
                        €{(selectedProduct.price * selectedQuantity).toFixed(2)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddToCart(selectedProduct, selectedQuantity)}
                      className="w-full bg-accent text-accent-foreground font-bold uppercase tracking-wider px-6 py-4 rounded-lg hover:bg-primary hover:text-primary-foreground transition-all"
                    >
                      {i18n.language === 'bg' ? 'Добави избрания брой' : 'Add selected quantity'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Cart Sidebar */}
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetContent className="w-full sm:max-w-lg bg-background border-l border-border">
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl text-foreground">
                {t('shop.cart')}
              </SheetTitle>
              <SheetDescription className="text-muted-foreground">
                {cart.length === 0
                  ? (i18n.language === 'bg' ? 'Количката е празна' : 'Your cart is empty')
                  : `${getTotalItems()} ${i18n.language === 'bg' ? 'продукта' : 'items'}`}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-8 flex flex-col h-[calc(100vh-200px)]">
              <div className="flex-1 overflow-y-auto space-y-4">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 bg-secondary border border-border rounded-lg"
                  >
                    <img
                      src={item.image}
                      alt={i18n.language === 'bg' ? item.name_bg : item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">
                        {i18n.language === 'bg' ? item.name_bg : item.name}
                      </h4>
                      <p className="text-accent font-bold">€{item.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, -1)}
                          className="w-8 h-8 flex items-center justify-center border border-border rounded hover:bg-secondary"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center text-foreground">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, 1)}
                          className="w-8 h-8 flex items-center justify-center border border-border rounded hover:bg-secondary"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFromCart(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {cart.length > 0 && (
                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-heading text-xl text-foreground">{t('shop.total')}</span>
                    <span className="font-heading text-2xl font-bold text-accent">
                      €{getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={handleCheckout}
                    data-testid="checkout-button"
                    className="w-full bg-accent text-accent-foreground font-bold uppercase tracking-widest px-8 py-4 rounded-lg text-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                  >
                    {t('shop.checkout')}
                  </button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default ShopPage;
